import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Breadcrumbs, Card, EmptyValue, PageHeader, PageSkeleton, Skeleton } from "@/components/ui";
import { useActiveMembership } from "@/hooks/use-company-context";
import { useCompanyFormSubmission } from "@/hooks/use-company-form-submissions";
import { useTemplateVersion } from "@/hooks/use-company-form-template";
import { FormRenderer, parseFormDefinition } from "@njbsoft/form-renderer";
import { formatDateTime } from "@/lib/dates";

export const Route = createFileRoute("/_company/company/forms/submissions/$submissionId")({
  component: SubmissionDetailPage,
});

/**
 * Received-supplement detail. Fetches the supplement projection (now carrying
 * template_id + template_version_id + the verbatim responses document — the
 * backend addition that unblocked this surface), then resolves the pinned
 * version's definition via the family-version read and renders it READ-ONLY
 * through the shared FormRenderer so the reader sees exactly what the tester
 * answered. Company-private: utilities never see these answers.
 */
function SubmissionDetailPage() {
  const { t, i18n } = useTranslation(["company", "common"]);
  const { submissionId } = Route.useParams();
  const active = useActiveMembership();
  const companyId = active?.company_id ?? "";

  const submissionQuery = useCompanyFormSubmission(companyId, submissionId);
  const submission = submissionQuery.data;
  const versionQuery = useTemplateVersion(submission?.template_id, submission?.template_version_id);

  if (submissionQuery.isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (submissionQuery.isError || !submission) {
    return (
      <div className="p-8">
        <Breadcrumbs items={[{ label: t("forms.title"), to: "/company/forms" }]} />
        <PageHeader title={t("forms.loadDetailFailed")} />
      </div>
    );
  }

  const language: "en" | "es" = i18n.language.startsWith("es") ? "es" : "en";

  // The wire definition is an opaque JSON object; published versions passed
  // the strict publish gates, but guard on `sections` before handing the
  // document to the renderer.
  const definition = versionQuery.data
    ? parseFormDefinition(versionQuery.data.definition)
    : undefined;
  const renderable = definition !== undefined && Array.isArray(definition.sections);

  return (
    <div className="space-y-6 p-8">
      <Breadcrumbs
        items={[
          { label: t("forms.title"), to: "/company/forms" },
          { label: t("forms.submissionDetailTitle") },
        ]}
      />
      <PageHeader title={t("forms.submissionDetailTitle")} />

      <Card>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-muted">{t("forms.submissionColumns.tester")}</dt>
            <dd className="mt-0.5 text-on-surface">
              {submission.performed_by_name ?? <EmptyValue />}
            </dd>
          </div>
          <div>
            <dt className="text-muted">{t("forms.submissionColumns.receivedAt")}</dt>
            <dd className="mt-0.5 text-on-surface">{formatDateTime(submission.created_at)}</dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-muted">{t("forms.submissionPrivacyNote")}</p>
      </Card>

      <Card title={t("forms.submissionDetailTitle")}>
        {/* Read-only mount of the shared renderer. No facilityRefSlot: this
            portal's API surface has no org-facility lookup, so a facility_ref
            answer renders its stored id in a record box — the renderer's
            documented absent-slot fallback. No attachmentSlot: the company
            supplement wire carries no attachment listing. */}
        {versionQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-3/4" />
          </div>
        ) : renderable && definition ? (
          <FormRenderer
            definition={definition}
            answers={submission.responses}
            language={language}
          />
        ) : (
          <p className="text-sm text-muted">{t("forms.loadDetailFailed")}</p>
        )}
      </Card>
    </div>
  );
}
